const Icon = ({ d, className = "h-4 w-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
  </svg>
);

const CHEVRON = "M9 5l7 7-7 7";
const CHEVRON_DOWN = "M19 9l-7 7-7-7";

/**
 * One collapsible card, for every section of the lead sidecart.
 *
 * The sections were four hand-built copies of the same header that had
 * drifted apart: four count-badge palettes, three border colours, two button
 * sizes, a subtitle on three of them and a stray `mb-3` that pushed each
 * title off the row it sat in. Reading the sidecart meant re-learning the
 * chrome at every section.
 *
 * One header, one accent. The count is neutral, because a total is trivia.
 * `alert` is the single exception — unread messages are a to-do, so they get
 * the one colour in the card that means "act on this".
 *
 * The whole title row toggles, so the hit target is the header rather than a
 * 20px chevron. The section action sits outside that button, not nested in
 * it, so it needs no click-through guard.
 */
const SidecartSection = ({
  id,
  title,
  icon,
  count = 0,
  alert = null,
  action = null,
  expanded,
  onToggle,
  preview = null,
  children,
}) => (
  <section
    id={id}
    className="scroll-mt-32 rounded-lg border border-border bg-white"
  >
    <div className="flex items-center gap-2 px-4 py-2.5">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex min-w-0 flex-1 items-center gap-2 rounded text-left focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
      >
        <Icon
          d={CHEVRON}
          className={`h-4 w-4 flex-shrink-0 text-slate-400 transition-transform duration-200 ${
            expanded ? "rotate-90" : ""
          }`}
        />
        <Icon d={icon} className="h-4 w-4 flex-shrink-0 text-slate-400" />
        <span className="text-xs font-semibold uppercase tracking-wide text-primary">
          {title}
        </span>
        {alert ? (
          <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-800">
            {alert}
          </span>
        ) : count > 0 ? (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-text-secondary">
            {count}
          </span>
        ) : null}
      </button>
      {action}
    </div>

    {expanded ? (
      <div className="border-t border-border p-4">{children}</div>
    ) : preview ? (
      <div className="border-t border-border">{preview}</div>
    ) : null}
  </section>
);

/**
 * The section action — "New note", "New appointment".
 *
 * Outline, not filled. The sidecart keeps exactly one filled button, the Text
 * quick action; a filled button in every section would drain the accent.
 */
export const SectionAction = ({ open, onClick, label, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
      open
        ? "border-border text-text-secondary hover:bg-gray-50"
        : "border-teal-200 text-primary hover:border-teal-300 hover:bg-accent-teal-light-bg"
    }`}
  >
    <Icon
      d={open ? "M6 18L18 6M6 6l12 12" : "M12 4v16m8-8H4"}
      className="h-3.5 w-3.5"
    />
    {open ? "Cancel" : label}
  </button>
);

/** Foot of a collapsed preview: "Show all 4 appointments". */
export const ShowAllButton = ({ count, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center justify-center gap-1 rounded-b-lg py-2 text-sm font-medium text-primary transition-colors hover:bg-accent-teal-light-bg"
  >
    Show all {count} {label}
    <Icon d={CHEVRON_DOWN} className="h-4 w-4" />
  </button>
);

export default SidecartSection;
